import { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useLookupsContext } from "hooks/useContext";
import { actions } from "store/actions";
import useNotify from "hooks/useNotify";
import profileApi from "api/profile";
import { scrollIntoView } from "utils/helper";

function useCompanyDetails({ countryId } = {}) {
  const [t] = useTranslation();
  const userDetails = useSelector((state) => state.userDetails || {});
  const dispatch = useDispatch();
  const [isLoading, setIsLoading] = useState(false);

  const { countries, cities, getCities } = useLookupsContext(true);

  const { notifyError, notifySuccess } = useNotify();

  const countriesOptions = countries.map((elem) => ({
    ...elem,
    label: elem.name,
    value: elem.id,
  }));

  const citiesOptions = cities.map((elem) => ({
    ...elem,
    label: elem.name,
    value: elem.id,
  }));

  const onEditSave = async (values) => {
    try {
      setIsLoading(true);

      const {
        city: cityId,
        companyName,
        companyWebsite,
        country: countryId,
        email,
        phoneNumber: phone,
        representativeName,
      } = values;

      const payload = {
        companyName,
        companyWebsite,
        email,
        phone,
        countryId,
        cityId,
        representativeName,
      };

      await profileApi.updateCompanyDetails(userDetails?.agencyId, payload);

      const updatedData = {
        ...userDetails,
        ...values,
        country: countries.find((elem) => elem.id === values.country),
        city: cities.find((elem) => elem.id === values.city),
        phone: values.phoneNumber,
      };
      dispatch(actions.setUserDetails(updatedData));

      notifySuccess(t("companyDetailsUpdated"));
      scrollIntoView("companyDetailsSection");
    } catch (err) {
      console.error(err);
      notifyError(err?.data?.error ?? err?.message ?? "Something went wrong!");
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch cities when country changes
  useEffect(() => {
    if (countryId) {
      getCities(countryId);
    }
  }, [countryId]);

  return {
    onEditSave,
    countries,
    cities,
    countriesOptions,
    citiesOptions,
    isLoading,
  };
}

export default useCompanyDetails;
